"use client";

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { submitCrossPost } from "../../client/manager/cross-posting";
import { invalidateCrossPosting } from "./invalidation";
import type {
  CrossPostRow,
  PerPlatformResult,
  PostRequest,
} from "../../models/ayrshare";

type SubmitCrossPostResult = {
  post: CrossPostRow;
  perPlatform: PerPlatformResult[];
};

// Consumers do NOT pass idempotencyKey — the hook generates it (D-07).
export type SubmitCrossPostInput = Omit<PostRequest, "idempotencyKey">;

/**
 * Submit an immediate or scheduled cross-post to Ayrshare.
 *
 * Idempotency key generation lives at the hook boundary per CONTEXT.md D-07 —
 * the consumer does NOT pass one in. crypto.randomUUID() is available in
 * modern browsers (Chrome 92+, Safari 15.4+) and Node 19+ — no polyfill.
 *
 * Invalidates scope: "history" on success (D-05). The new post affects the
 * history list.
 *
 * Partial-failure is still a success from React Query's perspective (D-18) —
 * post.status === "partial_failed" carries the signal; consumer reads it from
 * result.data.post.status. Invalidation still runs so the history list shows
 * the new row with its partial-failed chip.
 *
 * Pessimistic per D-06 (no optimistic write). Returns raw useMutation result
 * per D-08.
 */
export function useSubmitCrossPost(): UseMutationResult<
  SubmitCrossPostResult,
  Error,
  SubmitCrossPostInput,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitCrossPostInput) =>
      submitCrossPost({
        ...input,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateCrossPosting(queryClient, {
        comedianId: variables.comedianId,
        scope: "history",
      });
    },
  });
}
